import { useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import type { ComponentInstance, RenderedNode } from '@ayumia/component-runtime';
import { text, type LocalizedText } from '../app/i18n';
import { PermissionDenied } from '../states/States';

/**
 * Renderer React du runtime de composants (ADR-031 §2, ADR-048). Il **affiche** l'arbre déjà
 * résolu par {@link ComponentInstance} et **transmet** les événements ; il n'évalue aucune
 * expression et n'exécute aucune action (59 §20.5). Chaque primitive du registre fermé a ici son
 * rendu ; un nom inconnu n'est jamais rendu en HTML brut.
 *
 * `visible`, `disabled`, `readOnly`, `opacity` restent de la présentation : le serveur décide.
 */

type Dispatch = (event: string, payload: Record<string, unknown>) => void;

const STYLE_MAP: Readonly<Record<string, keyof CSSProperties>> = { alignment: 'textAlign', shadow: 'boxShadow' };

function toStyle(style: Record<string, unknown>): CSSProperties {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(style)) {
    if (value === null || value === undefined || (typeof value !== 'string' && typeof value !== 'number')) continue;
    out[STYLE_MAP[key] ?? key] = value;
  }
  return out as CSSProperties;
}

function label(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (value !== null && typeof value === 'object') return text(value as LocalizedText);
  return '';
}

function bool(value: unknown): boolean {
  return value === true;
}

function handlers(node: RenderedNode, dispatch: Dispatch, current?: unknown): Record<string, unknown> {
  const on = new Set(node.events);
  const out: Record<string, unknown> = {};
  const now = (): string => new Date().toISOString();
  if (on.has('click')) out['onClick'] = () => dispatch('click', { timestamp: now() });
  if (on.has('double_click')) out['onDoubleClick'] = () => dispatch('double_click', { timestamp: now() });
  if (on.has('focus')) out['onFocus'] = () => dispatch('focus', { timestamp: now() });
  if (on.has('blur')) out['onBlur'] = () => dispatch('blur', { timestamp: now() });
  if (on.has('keydown')) out['onKeyDown'] = (event: React.KeyboardEvent) => dispatch('keydown', { key: event.key, timestamp: now() });
  if (on.has('keyup')) out['onKeyUp'] = (event: React.KeyboardEvent) => dispatch('keyup', { key: event.key, timestamp: now() });
  if (on.has('change') || on.has('input')) {
    out['onChange'] = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const target = event.target;
      const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target instanceof HTMLInputElement && target.type === 'number' ? (target.value === '' ? null : Number(target.value)) : target.value;
      const payload = { value, previous_value: current ?? null, checked: target instanceof HTMLInputElement ? target.checked : undefined, timestamp: now() };
      if (on.has('input')) dispatch('input', payload);
      if (on.has('change')) dispatch('change', payload);
    };
  }
  return out;
}

function Primitive({ node, dispatch, instance }: { node: RenderedNode; dispatch: Dispatch; instance: ComponentInstance }): ReactNode {
  const p = node.props;
  if (p['visible'] === false) return null;
  const style = toStyle(node.style);
  const children = node.children.map((child) => <NodeView key={child.id} node={child} instance={instance} />);
  const common = { 'data-node': node.id, style };

  switch (node.component) {
    case 'Container':
      return <div className="ay-container" {...common} {...handlers(node, dispatch)}>{children}</div>;
    case 'Stack':
      return <div className={`ay-stack ay-stack--${p['direction'] === 'row' ? 'row' : 'column'}`} {...common}>{children}</div>;
    case 'Grid':
      return <div className="ay-grid" {...common} style={{ ...style, gridTemplateColumns: `repeat(${typeof p['columns'] === 'number' ? p['columns'] : 2}, minmax(0, 1fr))` }}>{children}</div>;
    case 'Card':
      return (
        <section className={`ay-card${bool(p['selected']) ? ' ay-card--selected' : ''}`} aria-busy={bool(p['loading'])} {...common} {...handlers(node, dispatch)}>
          {p['title'] !== undefined && <h3 className="ay-card__title">{label(p['title'])}</h3>}
          {p['subtitle'] !== undefined && <p className="ay-card__subtitle">{label(p['subtitle'])}</p>}
          {children}
        </section>
      );
    case 'Text':
      return <p className={`ay-text ay-text--${typeof p['variant'] === 'string' ? p['variant'] : 'body'}`} {...common} {...handlers(node, dispatch)}>{label(p['text'])}</p>;
    case 'Heading': {
      const level = typeof p['level'] === 'number' && p['level'] >= 1 && p['level'] <= 4 ? p['level'] : 2;
      const Tag = `h${level}` as 'h2';
      return <Tag className="ay-heading" {...common}>{label(p['text'])}</Tag>;
    }
    case 'Button':
      return (
        <button type="button" className={`ay-button ay-button--${typeof p['variant'] === 'string' ? p['variant'] : 'primary'}`} disabled={bool(p['disabled']) || bool(p['loading'])} aria-busy={bool(p['loading'])} {...common} {...handlers(node, dispatch)}>
          {label(p['label'])}
        </button>
      );
    case 'Input':
    case 'NumberInput':
    case 'DatePicker': {
      const type = node.component === 'NumberInput' ? 'number' : node.component === 'DatePicker' ? (p['mode'] === 'datetime' ? 'datetime-local' : 'date') : typeof p['input_type'] === 'string' ? p['input_type'] : 'text';
      const value = p['value'] === null || p['value'] === undefined ? '' : String(p['value']);
      return (
        <label className="ay-field" {...common}>
          <span className="ay-field__label">
            {label(p['label'])}
            {bool(p['required']) && <span aria-hidden="true"> *</span>}
          </span>
          <span className="ay-field__control">
            <input type={type} value={value} placeholder={label(p['placeholder'])} disabled={bool(p['disabled'])} readOnly={bool(p['readOnly'])} required={bool(p['required'])} aria-invalid={p['error'] !== undefined && p['error'] !== null} {...handlers(node, dispatch, p['value'])} />
            {p['unit'] !== undefined && <span className="ay-field__unit">{label(p['unit'])}</span>}
          </span>
          {p['error'] !== undefined && p['error'] !== null && <span className="ay-field__error">{label(p['error'])}</span>}
          {p['help'] !== undefined && <span className="ay-field__help">{label(p['help'])}</span>}
        </label>
      );
    }
    case 'Select': {
      const options = Array.isArray(p['options']) ? (p['options'] as Array<{ value?: unknown; label?: unknown } | string>) : [];
      return (
        <label className="ay-field" {...common}>
          <span className="ay-field__label">{label(p['label'])}</span>
          <select value={p['value'] === null || p['value'] === undefined ? '' : String(p['value'])} disabled={bool(p['disabled']) || bool(p['readOnly'])} required={bool(p['required'])} {...handlers(node, dispatch, p['value'])}>
            <option value="">—</option>
            {options.map((option) => {
              const value = typeof option === 'string' ? option : String(option.value ?? '');
              return <option key={value} value={value}>{typeof option === 'string' ? option : label(option.label ?? option.value)}</option>;
            })}
          </select>
        </label>
      );
    }
    case 'Checkbox':
      return (
        <label className="ay-checkbox" {...common}>
          <input type="checkbox" checked={bool(p['checked'])} disabled={bool(p['disabled']) || bool(p['readOnly'])} {...handlers(node, dispatch, p['checked'])} />
          {label(p['label'])}
        </label>
      );
    case 'Badge':
      return <span className={`ay-badge ay-badge--${typeof p['variant'] === 'string' ? p['variant'] : 'neutral'}`} {...common}>{label(p['text'])}</span>;
    case 'Icon':
      return <span className="ay-icon" role="img" aria-label={label(p['label'] ?? p['name'])} {...common} {...handlers(node, dispatch)} />;
    case 'Image':
      return <img className="ay-image" src={typeof p['src'] === 'string' && /^(https:|\/|data:image\/)/.test(p['src']) ? p['src'] : undefined} alt={label(p['alt'])} {...common} />;
    case 'Divider':
      return <hr className="ay-divider" {...common} />;
    case 'Table': {
      const columns = Array.isArray(p['columns']) ? (p['columns'] as Array<{ field: string; label?: unknown }>) : [];
      const rows = Array.isArray(p['rows']) ? (p['rows'] as Array<Record<string, unknown>>) : [];
      if (rows.length === 0) return <p className="ay-text ay-text--muted" {...common}>{label(p['empty_text'] ?? 'Aucun élément.')}</p>;
      return (
        <table className="ay-table" {...common}>
          <thead>
            <tr>{columns.map((column) => <th key={column.field} scope="col">{label(column.label ?? column.field)}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={String(row['id'] ?? index)} onClick={node.events.includes('select') ? () => dispatch('select', { selected: row, timestamp: new Date().toISOString() }) : undefined}>
                {columns.map((column) => <td key={column.field}>{label(row[column.field])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case 'Tabs': {
      const tabs = Array.isArray(p['tabs']) ? (p['tabs'] as Array<{ id: string; label?: unknown }>) : [];
      return (
        <div className="ay-tabs" {...common}>
          <div role="tablist">
            {tabs.map((tab) => (
              <button key={tab.id} type="button" role="tab" aria-selected={p['selected'] === tab.id} onClick={() => dispatch('select', { selected: tab.id, timestamp: new Date().toISOString() })}>
                {label(tab.label ?? tab.id)}
              </button>
            ))}
          </div>
          <div role="tabpanel">{children}</div>
        </div>
      );
    }
    case 'Timeline': {
      const items = Array.isArray(p['items']) ? (p['items'] as Array<{ id?: string; label?: unknown; at?: unknown }>) : [];
      return (
        <ol className="ay-timeline" {...common}>
          {items.map((item, index) => <li key={item.id ?? index}><span>{label(item.at)}</span> {label(item.label)}</li>)}
        </ol>
      );
    }
    case 'Modal':
    case 'Drawer':
      if (!bool(p['open'])) return null;
      return (
        <div className={node.component === 'Modal' ? 'ay-dialog ay-dialog--md' : `ay-drawer ay-drawer--${p['side'] === 'left' ? 'left' : 'right'}`} role="dialog" aria-label={label(p['title'])} {...common}>
          <header className="ay-dialog__header">
            <h2>{label(p['title'])}</h2>
            {node.events.includes('close') && <button type="button" className="ay-icon-button" aria-label="Fermer" onClick={() => dispatch('close', {})}>×</button>}
          </header>
          <div className="ay-dialog__content">{children}</div>
        </div>
      );
    default:
      return <div className="ay-state ay-state--error" role="alert">{`Primitive non prise en charge : ${node.component}`}</div>;
  }
}

function NodeView({ node, instance }: { node: RenderedNode; instance: ComponentInstance }): ReactNode {
  if (node.error !== undefined) return <div className="ay-state ay-state--error" role="alert" data-node={node.id}>{node.error.message}</div>;
  const dispatch: Dispatch = (event, payload) => void instance.dispatch(node.id, event, payload);
  if (node.kind === 'component') {
    const child = instance.childInstance(node.id);
    if (child === undefined) return <div className="ay-state ay-state--error" role="alert">{`Composant indisponible : ${node.component}`}</div>;
    return <ComponentHost instance={child} />;
  }
  return <Primitive node={node} dispatch={dispatch} instance={instance} />;
}

export function ComponentHost({ instance }: { instance: ComponentInstance }): ReactNode {
  const snapshot = useSyncExternalStore(
    (listener) => instance.subscribe(listener),
    () => instance.snapshot(),
  );
  return (
    <div className="ay-component" data-component={instance.manifest.component.name} aria-busy={snapshot.status === 'busy'}>
      {snapshot.status === 'permission_denied' && <PermissionDenied message={snapshot.error?.message ?? 'Action refusée.'} />}
      {snapshot.status === 'error' && snapshot.error !== undefined && <div className="ay-banner ay-banner--danger" role="alert">{snapshot.error.message}</div>}
      <NodeView node={snapshot.tree} instance={instance} />
    </div>
  );
}
